import { T, useTranslation } from "@redotech/redo-i18n";
import AlertTriangleIcon from "@redotech/redo-icons/alert-triangle.svg";
import ArrowRightIcon from "@redotech/redo-icons/arrow-right.svg";
import CheckIcon from "@redotech/redo-icons/check.svg";
import CopyIcon from "@redotech/redo-icons/copy-01.svg";
import ExternalLinkIcon from "@redotech/redo-icons/link-external-02.svg";
import PlayIcon from "@redotech/redo-icons/play.svg";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoCheckbox } from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox";
import {
  extractValues,
  type RedoTagDelimiter,
  RedoTagInputField,
} from "@redotech/redo-web/arbiter-components/input/redo-tag-input-field";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import type { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoRadioGroup } from "@redotech/redo-web/arbiter-components/radio/redo-radio-group";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import { Flex } from "@redotech/redo-web/flex";
import type { ComponentType, ReactNode } from "react";
import { memo, useState } from "react";
import type {
  ChargeType,
  FormState,
  ValidationErrors,
  WithdrawalsOnboardingAddressOption,
} from "./withdrawals-onboarding-modal";
import * as styles from "./withdrawals-onboarding-modal.module.css";
import { buildPolicy } from "./withdrawals-policy";

interface UpdateFormProps {
  form: FormState;
  update: (patch: Partial<FormState>) => void;
}

export const StepHeading = memo(function StepHeading({
  eyebrow,
  title,
  intro,
}: {
  eyebrow?: string;
  title: string;
  intro?: ReactNode;
}) {
  return (
    <div className={styles.heading}>
      {eyebrow && <p className={styles.eyebrow}>{eyebrow}</p>}
      <h1 className={styles.stepTitle}>{title}</h1>
      {intro && <p className={styles.intro}>{intro}</p>}
    </div>
  );
});

export const SubHeading = memo(function SubHeading({
  children,
}: {
  children: ReactNode;
}) {
  return <h2 className={styles.subHeading}>{children}</h2>;
});

export const ChoiceCard = memo(function ChoiceCard({
  title,
  description,
  onClick,
}: {
  title: string;
  description: string;
  onClick: () => void;
}) {
  return (
    <button className={styles.choiceCard} onClick={onClick} type="button">
      <span className={styles.choiceText}>
        <span className={styles.choiceTitle}>{title}</span>
        <span className={styles.choiceDescription}>{description}</span>
      </span>
      <ArrowRightIcon className={styles.choiceIcon} />
    </button>
  );
});

export const FieldCard = memo(function FieldCard({
  title,
  titleAccessory,
  description,
  children,
}: {
  title: string;
  titleAccessory?: ReactNode;
  description?: string;
  children?: ReactNode;
}) {
  return (
    <div className={styles.fieldCard}>
      <div className={styles.fieldHeader}>
        <div className={styles.inlineTitle}>
          <h3 className={styles.fieldTitle}>{title}</h3>
          {titleAccessory}
        </div>
        {description && (
          <p className={styles.fieldDescription}>{description}</p>
        )}
      </div>
      {children}
    </div>
  );
});

export const CheckList = memo(function CheckList({
  items,
}: {
  items: string[];
}) {
  return (
    <ul className={styles.checkList}>
      {items.map((item) => (
        <li className={styles.listItem} key={item}>
          <span className={styles.checkIcon}>
            <CheckIcon className={styles.checkSvg} />
          </span>
          <span className={styles.checkText}>{item}</span>
        </li>
      ))}
    </ul>
  );
});

export const Bullets = memo(function Bullets({ items }: { items: string[] }) {
  return (
    <ul className={styles.bullets}>
      {items.map((item) => (
        <li className={styles.listItem} key={item}>
          <span className={styles.bullet} />
          <span className={styles.bulletText}>{item}</span>
        </li>
      ))}
    </ul>
  );
});

export const Callout = memo(function Callout({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div className={styles.callout}>
      <div className={styles.inlineTitle}>
        <AlertTriangleIcon className={styles.warningIcon} />
        <span className={styles.calloutLabel}>{label}</span>
      </div>
      <div className={styles.calloutBody}>{children}</div>
    </div>
  );
});

export const NoteCard = memo(function NoteCard({
  icon,
  title,
  children,
}: {
  icon: ComponentType<{ className?: string }>;
  title: string;
  children: ReactNode;
}) {
  const NoteIcon = icon;
  return (
    <div className={styles.noteCard}>
      <div className={styles.inlineTitle}>
        <NoteIcon className={styles.noteIcon} />
        <h4 className={styles.noteTitle}>{title}</h4>
      </div>
      <div className={styles.noteBody}>{children}</div>
    </div>
  );
});

export const VimeoVideo = memo(function VimeoVideo({
  src,
  label,
}: {
  src: string;
  label: string;
}) {
  const { t } = useTranslation();
  const [isPlaying, setIsPlaying] = useState(false);

  if (isPlaying) {
    return (
      <div className={styles.videoEmbed}>
        <iframe
          allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
          allowFullScreen
          className={styles.videoIframe}
          src={src}
          title={label}
        />
      </div>
    );
  }

  return (
    <button
      aria-label={t("Play {{label}}", { label })}
      className={styles.videoPlaceholder}
      onClick={() => setIsPlaying(true)}
      type="button"
    >
      <span className={styles.playCircle}>
        <PlayIcon className={styles.playIcon} />
      </span>
      <span className={styles.videoLabel}>{label}</span>
    </button>
  );
});

export const FulfillmentStates = memo(function FulfillmentStates() {
  const { t } = useTranslation();
  return (
    <div className={styles.fulfillmentRows}>
      <FulfillmentRow
        badge={t("Unfulfilled")}
        badgeColor="gray"
        name={t("1. Whole order only")}
      >
        <p className={styles.fulfillmentBodyText}>
          {t(
            "When an order has not shipped yet, a shopper can exercise their right of withdrawal and the entire order is cancelled. Redo handles this automatically; no return shipping label is needed.",
          )}
        </p>
        <p className={styles.fulfillmentBodyText}>
          <T>
            <strong className={styles.strongText}>Important:</strong> For
            unfulfilled orders, Redo only supports withdrawing the entire order.
            This is the most common withdrawal scenario.
          </T>
        </p>
      </FulfillmentRow>
      <FulfillmentRow
        badge={t("Fully fulfilled")}
        badgeColor="success"
        name={t("2. Individual line items supported")}
      >
        <p className={styles.fulfillmentBodyText}>
          {t(
            "When all items in an order have shipped, shoppers can withdraw individual line items or the entire order. Redo generates a prepaid return label for the items being returned.",
          )}
        </p>
      </FulfillmentRow>
      <FulfillmentRow
        badge={t("Partially fulfilled")}
        badgeColor="warning"
        name={t("3. Flagged for your review")}
      >
        <p className={styles.fulfillmentBodyText}>
          {t(
            "When some items have shipped and others have not, split fulfillments create ambiguity about the correct refund amount that Redo cannot automatically resolve.",
          )}
        </p>
        <p className={styles.fulfillmentBodyText}>
          <T>
            Redo creates the request and notifies the shopper, but{" "}
            <strong className={styles.strongText}>
              no automatic action is taken in Shopify
            </strong>
            . The request is flagged as{" "}
            <strong className={styles.strongText}>Needs attention</strong> for
            your team to review. This is why we ask for a notification email
            during setup.
          </T>
        </p>
      </FulfillmentRow>
    </div>
  );
});

const FulfillmentRow = memo(function FulfillmentRow({
  badge,
  badgeColor,
  name,
  children,
}: {
  badge: string;
  badgeColor: "gray" | "success" | "warning";
  name: string;
  children: ReactNode;
}) {
  return (
    <div className={styles.fulfillmentRow}>
      <div className={styles.rowHeader}>
        <span className={styles.fulfillmentTitle}>{name}</span>
        <RedoBadge color={badgeColor} size="sm" text={badge} />
      </div>
      <div className={styles.fulfillmentBody}>{children}</div>
    </div>
  );
});

export const ShippingSection = memo(function ShippingSection({
  addressOptionsError,
  form,
  returnAddressOptions,
  settingsHref,
  update,
  validationError,
}: UpdateFormProps & {
  addressOptionsError: boolean;
  returnAddressOptions: WithdrawalsOnboardingAddressOption[];
  settingsHref: string;
  validationError?: string;
}) {
  const { t } = useTranslation();
  const options = returnAddressOptions.map<RedoListItem<string>>((option) => ({
    id: option.id,
    value: option.id,
    type: "text",
    text: option.label,
    supportingText: option.supportingText,
  }));
  const selected = options.find((option) => option.id === form.returnAddress);
  return (
    <FieldCard
      description={t(
        "Most merchants only need to confirm the address where withdrawn items should be sent. If you ship certain items to different locations, you can add conditions later, but for most, a single return address is all that is needed.",
      )}
      title={t("Configure shipping")}
    >
      <RedoSingleSelectDropdownInput
        description={validationError}
        fullWidth
        label={t("Return address")}
        options={options}
        optionSelected={({ item }) =>
          update({ returnAddress: String(item.id) })
        }
        placeholder={t("Select a return address")}
        selectedItem={selected}
        size="md"
        state={validationError ? "error" : "default"}
      />
      {addressOptionsError ? (
        <p className={styles.fieldError}>
          {t(
            "Return addresses could not be loaded. Open shipping settings to confirm your saved addresses, then try again.",
          )}
        </p>
      ) : null}
      <ShippingSettingsLink href={settingsHref} />
    </FieldCard>
  );
});

export const BillingSection = memo(function BillingSection({
  billingChecked,
  billingReady,
  onCheckBilling,
  pending,
  settingsHref,
}: {
  billingChecked: boolean;
  billingReady: boolean;
  onCheckBilling: () => void;
  pending: boolean;
  settingsHref: string;
}) {
  const { t } = useTranslation();
  return (
    <FieldCard
      description={t(
        "Add a billing method to cover the cost of return shipping labels generated during the withdrawal process.",
      )}
      title={t("Add billing for labels")}
      titleAccessory={
        <RedoBadge
          color={billingReady ? "success" : "gray"}
          size="sm"
          text={billingReady ? t("Complete") : t("Required")}
        />
      }
    >
      <Flex align="center" gap="md" wrap="wrap">
        <BillingSettingsLink href={settingsHref} />
        <RedoButton
          hierarchy="secondary"
          onClick={() => onCheckBilling()}
          pending={pending}
          size="sm"
          text={billingReady ? t("Check again") : t("Check billing")}
        />
      </Flex>
      {billingReady ? (
        <div className={styles.statusRow}>
          <span className={styles.statusIconSuccess}>
            <CheckIcon className={styles.statusIconSvg} />
          </span>
          <span className={styles.statusText}>
            {t("Billing is set up for return shipping labels.")}
          </span>
        </div>
      ) : billingChecked ? (
        <div className={styles.statusRow}>
          <span className={styles.statusIconWarning}>
            <AlertTriangleIcon className={styles.statusIconSvg} />
          </span>
          <span className={styles.statusText}>
            {t("Billing still needs to be set up before you can continue.")}
          </span>
        </div>
      ) : (
        <p className={styles.helperText}>
          {t(
            "After adding billing in the new tab, return here and check billing.",
          )}
        </p>
      )}
    </FieldCard>
  );
});

export const NotificationsSection = memo(function NotificationsSection({
  form,
  settingsHref,
  update,
  validationError,
}: UpdateFormProps & { settingsHref: string; validationError?: string }) {
  const { t } = useTranslation();
  return (
    <FieldCard
      description={t(
        "Some withdrawal requests, particularly those for partially fulfilled orders, cannot be processed automatically and require manual review. When a shopper requests withdrawal on a partially fulfilled order, Redo creates the request and notifies the shopper but takes no automatic action in Shopify; your team processes the refund manually, and this email is how we surface those requests.",
      )}
      title={t("Set up withdrawal notifications")}
    >
      <RedoTextInput
        errorText={validationError}
        fullWidth
        label={t("Merchant notification recipients")}
        placeholder="ops@example.com, support@example.com"
        setValue={(value) => update({ notificationEmail: value })}
        size="md"
        state={validationError ? "error" : "default"}
        value={form.notificationEmail}
      />
      <NotificationSettingsLink href={settingsHref} />
    </FieldCard>
  );
});

export const FulfillmentNotifySection = memo(function FulfillmentNotifySection({
  form,
  update,
  validationError,
}: UpdateFormProps & { validationError?: string }) {
  const { t } = useTranslation();
  const [tagInput, setTagInput] = useState("");
  const delimiters: RedoTagDelimiter[] = ["newline", "comma"];
  const commitPendingEmail = () => {
    const emails = extractValues(tagInput, delimiters);
    if (emails.length > 0) {
      update({ fulfillmentEmails: [...form.fulfillmentEmails, ...emails] });
    }
    setTagInput("");
  };

  return (
    <FieldCard
      description={t(
        "Do you need to notify 3PLs, WMS systems, or ERPs for canceled orders to stop fulfillment? If so, we will email them whenever a withdrawal cancels an order.",
      )}
      title={t("Notify fulfillment systems on cancellation")}
    >
      <RedoCheckbox
        label={t("Notify my 3PL / WMS / ERP when an order is canceled")}
        setValue={(value) => update({ fulfillmentNotify: value === true })}
        size="sm"
        value={form.fulfillmentNotify}
      />
      {form.fulfillmentNotify && (
        <RedoTagInputField
          delimiters={delimiters}
          errorText={validationError}
          inputValue={tagInput}
          label={t("Third-party fulfillment recipients")}
          onBlur={commitPendingEmail}
          placeholder="fulfillment@example.com"
          setInputValue={setTagInput}
          setTags={(tags) => update({ fulfillmentEmails: tags })}
          size="md"
          state={validationError ? "error" : "default"}
          tags={form.fulfillmentEmails.map((email) => ({ text: email }))}
        />
      )}
    </FieldCard>
  );
});

const ChargeShippingSetting = memo(function ChargeShippingSetting({
  form,
  update,
  validationError,
  withdrawalsFlowSettingsHref,
}: UpdateFormProps & {
  validationError?: string;
  withdrawalsFlowSettingsHref: string;
}) {
  const { t } = useTranslation();
  return (
    <Flex dir="column" gap="md">
      <RedoToggle
        label={t("Do you plan to charge shoppers for return shipping?")}
        semantic
        setValue={(value) => update({ chargeShipping: value })}
        size="sm"
        value={form.chargeShipping}
      />
      {form.chargeShipping && (
        <div className={styles.indented}>
          <RedoRadioGroup<ChargeType>
            gap="sm"
            options={[
              { id: "actual", label: t("Charge the actual shipping cost") },
              { id: "flat", label: t("Charge a flat rate") },
            ]}
            setValue={(value) => update({ chargeType: value })}
            size="sm"
            value={form.chargeType}
          />
          {form.chargeType === "flat" && (
            <>
              <RedoTextInput
                errorText={validationError}
                fullWidth
                label={t("Flat return shipping rate")}
                placeholder={t("e.g. {{example}}", { example: "EUR 5.00" })}
                setValue={(value) => update({ chargeAmount: value })}
                size="md"
                state={validationError ? "error" : "default"}
                value={form.chargeAmount}
              />
              <p className={styles.helperText}>
                <T>
                  Need different flat rates per currency? Configure them{" "}
                  <a
                    className={styles.inlineLink}
                    href={withdrawalsFlowSettingsHref}
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    here
                    <ExternalLinkIcon className={styles.pseudoLinkIcon} />
                  </a>
                </T>
              </p>
            </>
          )}
        </div>
      )}
    </Flex>
  );
});

export const PolicySection = memo(function PolicySection({
  form,
  update,
  validationErrors,
  withdrawalsFlowSettingsHref,
  withdrawalPortalLink,
}: UpdateFormProps & {
  validationErrors: ValidationErrors;
  withdrawalsFlowSettingsHref: string;
  withdrawalPortalLink: string;
}) {
  const { t } = useTranslation();
  const policyText = buildPolicy(form, withdrawalPortalLink);
  return (
    <FieldCard
      description={t(
        "Every merchant selling in the EU is legally required to publish a withdrawal FAQ. Answer the two questions below and we will generate the exact policy text for your setup.",
      )}
      title={t("Update your return policy")}
    >
      <Flex dir="column" gap="lg">
        <ChargeShippingSetting
          form={form}
          update={update}
          validationError={validationErrors.chargeAmount}
          withdrawalsFlowSettingsHref={withdrawalsFlowSettingsHref}
        />
        <RedoToggle
          label={t("Are you offering withdrawal coverage for return shipping?")}
          semantic
          setValue={(value) => update({ policyCoverage: value })}
          size="sm"
          value={form.policyCoverage}
        />
      </Flex>
      <div className={styles.policyPreview}>
        <div className={styles.policyHeader}>
          <p className={styles.policyTitle}>Right of Withdrawal</p>
          <CopyButton getText={() => policyText} label={t("Copy template")} />
        </div>
        <p className={styles.policyBody}>
          {policyText.replace(/^Right of Withdrawal\n\n/, "")}
        </p>
      </div>
    </FieldCard>
  );
});

export const WithdrawalLinkSection = memo(function WithdrawalLinkSection({
  withdrawalPortalLink,
}: {
  withdrawalPortalLink: string;
}) {
  const { t } = useTranslation();
  return (
    <FieldCard
      description={t(
        "Customers need a clear way to start a withdrawal. Add your withdrawal link to your website footer. You can reference this again anytime under Settings > Customer Portal > Right of Withdrawal section.",
      )}
      title={t("Add a withdrawal button to your website")}
    >
      <div className={styles.linkPreview}>
        <code className={styles.codeText}>{withdrawalPortalLink}</code>
        <CopyButton
          getText={() => withdrawalPortalLink}
          label={t("Copy link")}
        />
      </div>
    </FieldCard>
  );
});

export const CoverageCard = memo(function CoverageCard() {
  const { t } = useTranslation();
  return (
    <div className={styles.coverageCard}>
      <div className={styles.inlineTitle}>
        <h3 className={styles.coverageTitle}>
          {t("Interested in withdrawal coverage?")}
        </h3>
        <RedoBadge color="blue" size="sm" text={t("Optional")} />
      </div>
      <p className={styles.coverageDescription}>
        {t(
          "Withdrawal is included free for all Redo merchants. Once you are live, you can enable paid withdrawal coverage, where customers pay a small fee to have return shipping covered as part of their purchase. This is separate from the free compliance feature and is always your choice to offer.",
        )}
      </p>
      <Bullets
        items={[
          t(
            "Must be labeled withdrawal coverage in your policy and at checkout",
          ),
          t(
            "If you charge for return shipping as part of coverage, turn on both policy questions",
          ),
          t(
            "No pressure to offer this; compliance is fully covered by the free feature",
          ),
        ]}
      />
    </div>
  );
});

const CopyButton = memo(function CopyButton({
  getText,
  label,
}: {
  getText: () => string;
  label: string;
}) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  return (
    <RedoButton
      hierarchy="secondary"
      IconLeading={copied ? CheckIcon : CopyIcon}
      onClick={() => {
        void navigator.clipboard?.writeText(getText());
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }}
      size="sm"
      text={copied ? t("Copied") : label}
    />
  );
});

interface NumberedStep {
  title: string;
  description: string;
  link?: { href: string; text: string; external: boolean };
  copy?: { getText: () => string; label: string };
}

export const NumberedSteps = memo(function NumberedSteps({
  steps,
}: {
  steps: NumberedStep[];
}) {
  return (
    <ol className={styles.numberedSteps}>
      {steps.map((step, index) => (
        <li className={styles.numberedStep} key={step.title}>
          <span className={styles.numberBadge}>{index + 1}</span>
          <div className={styles.numberedStepBody}>
            <span className={styles.numberedStepTitle}>{step.title}</span>
            <span className={styles.numberedStepDescription}>
              {step.description}
            </span>
          </div>
          {(step.copy || step.link) && (
            <div className={styles.numberedStepCta}>
              {step.copy && (
                <CopyButton
                  getText={step.copy.getText}
                  label={step.copy.label}
                />
              )}
              {step.link && (
                <ConfirmationFieldLink
                  external={step.link.external}
                  href={step.link.href}
                  text={step.link.text}
                />
              )}
            </div>
          )}
        </li>
      ))}
    </ol>
  );
});

export const ConfirmationFieldLink = memo(function ConfirmationFieldLink({
  external,
  href,
  text,
}: {
  external: boolean;
  href: string;
  text: string;
}) {
  return (
    <a
      className={styles.pseudoLink}
      href={href}
      rel={external ? "noopener noreferrer" : undefined}
      target={external ? "_blank" : undefined}
    >
      {text}
      {external ? (
        <ExternalLinkIcon className={styles.pseudoLinkIcon} />
      ) : (
        <ArrowRightIcon className={styles.pseudoLinkIcon} />
      )}
    </a>
  );
});

const ShippingSettingsLink = memo(function ShippingSettingsLink({
  href,
}: {
  href: string;
}) {
  const { t } = useTranslation();
  return (
    <a
      className={styles.pseudoLink}
      href={href}
      rel="noopener noreferrer"
      target="_blank"
    >
      {t("Open shipping settings in Redo")}
      <ExternalLinkIcon className={styles.pseudoLinkIcon} />
    </a>
  );
});

const BillingSettingsLink = memo(function BillingSettingsLink({
  href,
}: {
  href: string;
}) {
  const { t } = useTranslation();
  return (
    <a
      className={styles.pseudoLink}
      href={href}
      rel="noopener noreferrer"
      target="_blank"
    >
      {t("Open billing settings in Redo")}
      <ExternalLinkIcon className={styles.pseudoLinkIcon} />
    </a>
  );
});

const NotificationSettingsLink = memo(function NotificationSettingsLink({
  href,
}: {
  href: string;
}) {
  const { t } = useTranslation();
  return (
    <a
      className={styles.pseudoLink}
      href={href}
      rel="noopener noreferrer"
      target="_blank"
    >
      {t("Open notification settings in Redo")}
      <ExternalLinkIcon className={styles.pseudoLinkIcon} />
    </a>
  );
});
